import { useEffect, useRef } from 'react';
import {
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

export type UseQueryWithErrorHandlerOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> = UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> & {
  onError?: (error: TError) => void;
};

/**
 * `useQuery` plus the per-query `onError` callback that TanStack Query v5 removed.
 * `onError` runs once for every fetch that fails, like it did in react-query v3.
 */
export function useQueryWithErrorHandler<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>({
  onError,
  ...options
}: UseQueryWithErrorHandlerOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey
>): UseQueryResult<TData, TError> {
  const query = useQuery<TQueryFnData, TError, TData, TQueryKey>(options);
  const { error, errorUpdatedAt } = query;

  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });

  // Errors already in the cache when this observer mounted were not caused by it.
  const initialErrorUpdatedAt = useRef(errorUpdatedAt);
  useEffect(() => {
    if (error && errorUpdatedAt !== initialErrorUpdatedAt.current) {
      onErrorRef.current?.(error);
    }
  }, [error, errorUpdatedAt]);

  return query;
}
